import React, { useCallback, useEffect, useRef, useState } from 'react';
import buildStyle from '../build-path.module.scss';
import './file.scss';
import {
  IonButton,
  IonCol,
  IonGrid,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonModal,
  IonRow,
  IonSelect,
  IonSelectOption,
  IonText,
} from '@ionic/react';
import BuildGenerateCrate from '../../build-generate-crate/build-generate-crate';
import { iTrack } from '../../../../types/ITrack';
import { GridReadyEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import { iCrate } from '../../../../types/ICrate';
import { useHistory } from 'react-router-dom';
import { useDispatch } from '../../../../store/hooks';
import { settingsOutline } from 'ionicons/icons';

export default function FilePath() {
  const theme = useSelector((state: RootState) => state.themeState.theme);
  const [fileUpload, setFileUpload] = useState<any>(null);
  const [fileContent, setFileContent] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [crateName, setCrateName] = useState('');
  const [tracks, setTracks] = useState<iTrack[]>([]);
  const [chosenDelimiter, setChosenDelimiter] = useState<string>('');
  const [gridApi, setGridApi] = useState<any>();
  const [showDelimiterChange, setShowDelimiterChange] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [columnOptions, setColumnOptions] = useState<string[]>([]);
  const [artistColumn, setArtistColumn] = useState<string>('');
  const [titleColumn, setTitleColumn] = useState<string>('');

  const history = useHistory();
  const dispatch = useDispatch();

  const delimiters: { [key: string]: string } = {
    Comma: ',',
    Hyphen: '-',
    Tab: '\t',
    Pipe: '|',
    Semicolon: ';',
  };

  const buildCrate = () => {
    const newCrate: iCrate = {
      _id: '',
      Title: crateName,
      Description: 'Created from Crate Hackers Crate Builder (file import)',
      Tracks: tracks,
      createdFrom: 'Text',
    };

    return newCrate;
  };

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLIonInputElement>) => {
      event.preventDefault();
      event.stopPropagation();
    },
    [],
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLIonInputElement>) => {
      event.preventDefault();
      event.stopPropagation();
      const files = event.dataTransfer.files;

      if (files.length) {
        const file = files[0];

        if (!file) return;
        if (!file.name.toLowerCase().endsWith('.csv')) {
          alert('Please upload a valid CSV file.');
          return;
        }

        setFileUpload(file);
        processFile(file);
      }
    },
    [],
  );

  const processFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result;
      if (typeof content === 'string') {
        setFileContent(content);
        parseContent(content);
      } else {
        // TODO: Throw some type of error or display an error on screen for it not being readable
      }
    };
    reader.readAsText(file);
  };

  const parseContent = (content: string) => {
    const lines = content
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line && line.replace(/[,;\t|-]/g, '').trim() !== '');

    if (lines.length === 0) {
      setTracks([]);
      return;
    }

    const firstLine = lines[0];
    const delimiter = detectDelimiter(firstLine);
    setChosenDelimiter(delimiter);

    const columns = firstLine.split(delimiter).map((col) => col.trim());

    const nonEmptyColumns = columns.filter((col) => col !== '');
    setColumnOptions(nonEmptyColumns);

    if (!artistColumn || !titleColumn) {
      const artistCol = nonEmptyColumns.find(
        (col) =>
          col.toLowerCase().includes('artist') ||
          col.toLowerCase().includes('band'),
      );
      const titleCol = nonEmptyColumns.find(
        (col) =>
          col.toLowerCase().includes('song') ||
          col.toLowerCase().includes('title') ||
          col.toLowerCase().includes('name'),
      );

      if (artistCol) setArtistColumn(artistCol);
      if (titleCol) setTitleColumn(titleCol);
    }

    let startIndex = 0;

    if (
      nonEmptyColumns.length > 0 &&
      (nonEmptyColumns.some((col) => col.toLowerCase().includes('artist')) ||
        nonEmptyColumns.some((col) => col.toLowerCase().includes('song')) ||
        nonEmptyColumns.some((col) => col.toLowerCase().includes('title')))
    ) {
      startIndex = 1;
    }

    const newTracks = lines
      .slice(startIndex)
      .map((line) => parseTrack(line, delimiter))
      .filter((track): track is iTrack => track !== null);

    const filteredTracks = newTracks.filter(
      (track) => track.Artist.trim() !== '' || track.Title.trim() !== '',
    );
    setTracks(filteredTracks);
  };

  const detectDelimiter = (line: string): string => {
    const delimiterValues = Object.keys(delimiters).map(
      (delimiterName: string) => delimiters[delimiterName],
    );

    const delimiterCounts = delimiterValues.map((delimiter) => ({
      delimiter,
      count: (
        line.match(
          new RegExp(delimiter.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
        ) || []
      ).length,
    }));

    const bestDelimiter = delimiterCounts.reduce((prev, current) =>
      prev.count > current.count ? prev : current,
    );

    return bestDelimiter.count > 0 ? bestDelimiter.delimiter : ',';
  };

  const handleDelimiterChange = (newDelimiter: string) => {
    setChosenDelimiter(newDelimiter);
    if (fileContent) {
      parseContent(fileContent);
    }
  };

  useEffect(() => {
    if (fileContent) {
      parseContent(fileContent);
    } else {
      setTracks([]);
    }
  }, [chosenDelimiter, fileContent, artistColumn, titleColumn]);

  const parseTrack = (line: string, delimiter: string): iTrack | null => {
    if (
      line.trim() === '' ||
      line.replace(new RegExp(`[${delimiter}\\s]`, 'g'), '') === ''
    ) {
      return null;
    }

    const parts = line.split(delimiter).map((part) =>
      part
        .trim()
        .replace(/^["'](.*)["']$/, '$1')
        .trim(),
    );

    const nonEmptyParts = parts.filter((part) => part !== '');

    if (nonEmptyParts.length === 0) {
      return null;
    }

    let artistIdx = 0;
    let titleIdx = 1;

    if (columnOptions.length > 0 && artistColumn && titleColumn) {
      artistIdx = Math.max(0, columnOptions.indexOf(artistColumn));
      titleIdx = Math.max(1, columnOptions.indexOf(titleColumn));

      if (titleIdx === artistIdx) {
        titleIdx = artistIdx + 1;
      }
    }

    artistIdx = Math.min(artistIdx, parts.length - 1);
    titleIdx = Math.min(titleIdx, parts.length - 1);

    const artist = parts[artistIdx] || '';
    const title = parts[titleIdx] || '';

    if (!artist && !title && nonEmptyParts.length >= 2) {
      return { ID: '', Artist: nonEmptyParts[0], Title: nonEmptyParts[1] };
    }

    if (!artist && !title) {
      return null;
    }

    return { ID: '', Artist: artist, Title: title };
  };

  const changeMapping = () => {
    setShowModal(true);
  };

  const handleMappingSave = () => {
    setShowModal(false);

    // Re-parse the file content with the updated mapping
    if (fileContent) {
      parseContent(fileContent);
    }
  };

  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api);
  };

  const [columnDefs] = useState([
    { field: 'ID', hide: true },
    {
      field: 'Artist',
      headerName: 'Artist',
      flex: 1,
      resizable: true,
      filter: false,
      sortable: false,
      editable: false,
      draggable: false,
      lockPosition: true,
      suppressMenu: true,
      valueFormatter: (params: any) => {
        if (params.value === null || params.value === undefined) return '';
        return String(params.value);
      },
    },
    {
      field: 'Title',
      headerName: 'Title',
      flex: 1,
      resizable: true,
      filter: false,
      sortable: false,
      editable: false,
      draggable: false,
      lockPosition: true,
      suppressMenu: true,
      valueFormatter: (params: any) => {
        if (params.value === null || params.value === undefined) return '';
        return String(params.value);
      },
    },
  ]);

  return (
    <>
      <IonRow>
        <IonCol size="12">
          <div className={buildStyle.buildHeader}>
            <div>What should we name your crate?</div>
          </div>
          <IonInput
            className={buildStyle['build-input']}
            placeholder="Crate Name"
            labelPlacement="floating"
            fill="outline"
            value={crateName}
            onIonInput={(e: any) => setCrateName(e.target.value)}
          >
            <div slot="label">Crate Name</div>
          </IonInput>
        </IonCol>
      </IonRow>

      <IonRow>
        <IonCol size="12">
          <div className="file-input-row">
            <IonInput
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              className={buildStyle['build-input']}
              placeholder="Click browse to upload a file or drop"
              labelPlacement="stacked"
              fill="outline"
              value={fileUpload?.name || ''}
              onChange={(e: any) => {
                if (e.target.value === '') {
                  setFileUpload(null);
                  fileInputRef!.current!.value = '';
                }
              }}
            >
              <div slot="label">File</div>
            </IonInput>
            <IonButton
              className="text-primary-buttons file-browse-btn"
              onClick={() => {
                if (fileInputRef.current) {
                  fileInputRef.current.click();
                }
              }}
            >
              Browse
            </IonButton>
            <input
              ref={fileInputRef}
              type={'file'}
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
          </div>
          <IonText color="medium" className="file-support-text">
            Supported file extensions: CSV
          </IonText>
        </IonCol>
      </IonRow>

      {fileContent && (
        <>
          <IonRow>
            <IonCol size="12" sizeMd="6">
              {chosenDelimiter && (
                <IonText color="primary">
                  <h3>
                    Detected Delimiter:{' '}
                    {Object.keys(delimiters).find(
                      (key) => delimiters[key] === chosenDelimiter,
                    ) || 'None'}
                  </h3>
                </IonText>
              )}
            </IonCol>
            <IonCol>
              {/* Delimiter change functionality previously commented out */}
            </IonCol>
            <IonCol style={{ textAlign: 'right' }}>
              {tracks.length > 0 && (
                <IonButton
                  className="text-primary-buttons"
                  onClick={changeMapping}
                >
                  <IonIcon
                    icon={settingsOutline}
                    style={{ paddingRight: '8px' }}
                  />{' '}
                  Mapping
                </IonButton>
              )}
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol size="12">
              <div
                className="ag-theme-alpine"
                style={{ height: 386, width: '100%' }}
              >
                {tracks.length > 0 && (
                  <AgGridReact
                    className={`ag-theme-alpine${theme !== 'light' ? '-dark' : ''}`}
                    rowData={tracks}
                    columnDefs={columnDefs}
                    onGridReady={onGridReady}
                  />
                )}
              </div>
            </IonCol>
          </IonRow>
        </>
      )}

      <BuildGenerateCrate
        buildCrateFn={buildCrate}
        isBtnDisabled={crateName === '' || tracks.length === 0}
      />

      <IonModal
        isOpen={showModal}
        onDidDismiss={() => setShowModal(false)}
        id={'column-mapping'}
      >
        <IonItem>
          <IonLabel>Artist Column</IonLabel>
          <IonSelect
            value={artistColumn}
            placeholder="Select Artists"
            onIonChange={(e) => setArtistColumn(e.detail.value)}
          >
            {columnOptions.map((col, index) => (
              <IonSelectOption key={index} value={col}>
                {col}
              </IonSelectOption>
            ))}
          </IonSelect>
        </IonItem>
        <IonItem>
          <IonLabel>Title Column</IonLabel>
          <IonSelect
            value={titleColumn}
            placeholder="Select Titles"
            onIonChange={(e) => setTitleColumn(e.detail.value)}
          >
            {columnOptions.map((col, index) => (
              <IonSelectOption key={index} value={col}>
                {col}
              </IonSelectOption>
            ))}
          </IonSelect>
        </IonItem>
        <IonGrid>
          <IonRow>
            <IonCol className="button-col">
              <IonButton
                expand="block"
                className="mapping-button"
                color="light"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </IonButton>
            </IonCol>
            <IonCol className="button-col">
              <IonButton
                expand="block"
                className="mapping-button"
                onClick={handleMappingSave}
              >
                Save Mapping
              </IonButton>
            </IonCol>
          </IonRow>
        </IonGrid>
      </IonModal>
    </>
  );

  function handleFileChange(e: any) {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      alert('Please upload a valid CSV file.');
      return;
    }
    setFileUpload(file);
    processFile(file);
  }
}
